import React, { useRef } from "react";
import { Text } from "@react-three/drei";
import WingProfile from "../components/WingProfile";
import Divider from "../components/Divider";
import Case from "../components/Case";
import Wheel from "../components/Wheel";
import GlassWithHole from "../components/GlassWithHole";

import { useFoldingAnimation } from "../hooks/useFoldingAnimation";


const getDirectionIcon = (direction) => {
  switch (direction) {
    case 1: return "↲";
    case 4: return "↳";
    case 2: return "←";
    case 3: return "→";
    case 0: return "✕";
    default: return "?";
  }
};

const FoldingPanel = ({
  isExporting,
  panel,
  panelId,
  index,
  panels,
  width,
  height,
  position,
  rotation,
  isOpenTrigger,
  config,
  aluminumColor,
  glassColor,
  glassColor2
}) => {
  const direction = panel.direction;
  const vent = panel.vent;
  const combi = panel.combi;
  const almVent = panel.alm_vent;
  const groupRef = useRef();
  const { angle, pivotX } = useFoldingAnimation({
    panelId,
    index,
    panels,
    direction,
    width,
    isOpenTrigger,
    groupRef,
  });

  
  const thickness = 0.01;
  const isDouble = config.isDouble;
  const textPositionZ = isDouble ? (-thickness - 0.02) : (-thickness / 2 - 0.001);
  const safeHeight = parseFloat(height) || 0;
  const ringCount = Math.max(0, Math.floor(safeHeight / 0.01) - 10);
  const bazaWidth = isDouble ? width - 0.052 : width - 0.03;
  const araCitaPosition = isDouble ? (direction === 4) ? bazaWidth / 2 : -bazaWidth / 2 : ((direction === 4) ? width / 2 : -width / 2);

  return (
    <group position={position} rotation={rotation}>
      {/* Alt Kasa */}
      <Case isBottom={true} hasDropper={config.hasDropper} width={width} height={height} position={[0, 0, 0]} rotation={rotation} direction={direction} color={aluminumColor} />

      {/* PERDELİK PROFİL */}
      {config.hasDrapery && (
        <Case
          width={width}
          height={height}
          position={[0, height + 0.061, 0]}
          rotation={rotation}
          direction={direction}
          color={aluminumColor}
          isPerdelik={true} />
      )}

      {/* Üst Kasa */}
      <Case width={width} height={height} position={[0, height, 0]} rotation={rotation} direction={direction} color={aluminumColor} />

      {/* Cam + Alt/Üst Baza + Animasyonlu Grup */}
      <group ref={groupRef} position={[pivotX, 0, 0]} >
        <group position={[-pivotX, 0, 0]}>

          <GlassWithHole
            width={width - 0.01}
            height={height}
            thickness={thickness}
            color={glassColor}
            color2={glassColor2}
            isDouble={isDouble}
            config={config}
            combi={combi}
            almVent={almVent}
          />

          {/* ALT BAZA */}
          <WingProfile width={bazaWidth} depth={0.05} direction={direction} isKol={config.isKol} isDouble={isDouble} height={0.06} position={[0, 0.034, 0]} color={aluminumColor} />

          {/* ÜST BAZA */}
          <WingProfile width={bazaWidth} depth={0.05} direction={direction} isKol={config.isKol} isDouble={isDouble} height={0.06} position={[0, height - 0.034, 0]} color={aluminumColor} flip />


          {/* ZİNCİR */}
          {(direction === 1 || direction === 4) && !config.isKol && (
            <>
              {/* Zincir halkaları */}
              {[...Array(ringCount)].map((_, i) => (
                <mesh
                  key={i}
                  position={[(direction == 1 ? -width / 2 + 0.2 : width / 2 - 0.2), safeHeight - 0.05 - i * 0.01, isDouble ? -0.03 : -0.02]}
                  rotation={[Math.PI / 2, 0, 0]}
                >
                  <torusGeometry args={[0.001, 0.002, 4, 4]} />
                  <meshStandardMaterial color="black" />
                </mesh>
              ))}
            </>
          )}

          {/* YÖN İKONU - CAM ÜZERİNDE */}
          {!isExporting && (<Text
            position={[0, height / 2, textPositionZ]}
            fontSize={0.3}
            color="white"
            anchorX="center"
            anchorY="middle"
            rotation={[0, Math.PI, 0]}
          >
            {getDirectionIcon(direction)}
          </Text>)}

          {/* PANEL NUMARASI - CAM ÜZERİNDE */}
          {!isExporting && (<Text
            position={[width / 2 - 0.1, height - 0.2, textPositionZ]}
            fontSize={0.15}
            color="white"
            anchorX="center"
            anchorY="middle"
            rotation={[0, Math.PI, 0]}
          >
            {index + 1}
          </Text>)}

          {/* Havalandırma */}
          {vent && (
            <mesh
              position={[
                -width / 2 + vent.left_space / 1000,
                height - vent.top_space / 1000 - 0.1,
                0.00,
              ]}
              rotation={[Math.PI / 2, 0, 0]}
            >
              <cylinderGeometry args={[0.12, 0.12, isDouble ? 0.04 : 0.01, 64]} />
              <meshStandardMaterial color={"white"} />
            </mesh>
          )}

          {/* KOL */}
          {(direction === 1 || direction === 4) && config.isKol && (
            <mesh position={[direction === 1 ? -bazaWidth / 2 : bazaWidth / 2, height / 2, -0.015]}>
              <boxGeometry args={[0.015, 0.15, 0.05]} />
              <meshStandardMaterial color={"black"} />
            </mesh>
          )}

          {/* DİKEY PROFİL */}
          <Divider
            height={height - 0.175}
            position={[araCitaPosition, height / 2, 0]}
            rotation={[0, angle, 0]} // ← buraya dikkat!
            color={aluminumColor}
            isOpening={(direction === 1 || direction === 4) && config.isKol}
            isDouble={isDouble}
          />

          {(index == 0 && direction == 3) &&
            <Divider
              height={height - 0.175}
              position={[width / 2, height / 2, 0]}
              rotation={[0, angle, 0]} // ← buraya dikkat!
              color={aluminumColor}
              isOpening={(direction === 1 || direction === 4) && config.isKol}
              isDouble={isDouble}
            />
          }

          {/* DİKEY PROFİL */}
          {(isDouble && <Divider
            height={height - 0.175}
            position={[-araCitaPosition, height / 2, 0]}
            rotation={[0, angle, 0]} // ← buraya dikkat!
            color={aluminumColor}
            isOpening={direction === 1 || direction === 4}
            isDouble={isDouble}
          />
          )}
          {/* Tekerlekler */}
          {(direction === 2 || direction === 3) && <Wheel width={width} yPos={0} isLeft={direction == 3} index={index} panelCount={panels.length} />}
          {(direction === 2 || direction === 3) && <Wheel width={width} yPos={height} isLeft={direction == 3} isTop={true} index={index} panelCount={panels.length} />}
        </group>
      </group>
    </group>
  );
};

export default FoldingPanel;
